import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useLocation } from "react-router-dom";

import { getSessionItem } from "../utils/helper/session";

import { getRoleGrants } from "../services/wisdomServices/systemService";

import {
  buildPermissionMap,
  evaluateGrant,
  normalizeRole,
  permissionKey,
} from "./permissionUtils";

const PermissionContext = createContext(null);

/**
 * Extracts the permissions array from the possible
 * response formats returned by the permissions API.
 *
 * Supported formats:
 *
 * [
 *   {...},
 *   {...}
 * ]
 *
 * {
 *   data: [...]
 * }
 *
 * Axios:
 *
 * {
 *   data: {
 *     pagination: {...},
 *     data: [...]
 *   }
 * }
 *
 * @param {any} response
 * @returns {Array}
 */
const extractPermissions = (response) => {
  if (Array.isArray(response)) {
    return response;
  }

  if (Array.isArray(response?.data?.data)) {
    return response.data.data;
  }

  if (Array.isArray(response?.data)) {
    return response.data;
  }

  return [];
};

/**
 * Provides permission information and authorization helpers
 * throughout the application.
 *
 * Admin users with role === 1 have unrestricted access.
 *
 * For all other users, permissions are loaded from the API.
 *
 * The API already returns the permissions available for the
 * currently authenticated user:
 *
 * {
 *   endpoint: "/api/v1/managers",
 *   method: "GET",
 *   accessLevel: "full"
 * }
 *
 * @param {{ children: React.ReactNode }} props
 */
export function PermissionProvider({ children }) {
  const location = useLocation();

  const [user, setUser] = useState(() =>
    getSessionItem("User")
  );

  const [permissions, setPermissions] = useState([]);

  const [loading, setLoading] = useState(true);

  const [error, setError] = useState(null);

  /**
   * Refreshes the user whenever the route changes.
   *
   * sessionStorage itself is not reactive, so this ensures
   * that the provider gets the authenticated user after login.
   */
  useEffect(() => {
    const sessionUser =
      getSessionItem("User");

    setUser(sessionUser);
  }, [location.pathname]);

  const rawRole =
    user?.roleName ??
    user?.roleName;

  const role =
    normalizeRole(rawRole);

  const currentUserId =
    user?.id ??
    user?.userId;

  /**
   * Role 1 is Admin and has unrestricted access.
   */
  const isAdmin =
    Number(user?.roleName) === 1;

  /**
   * Loads permissions for the currently authenticated user.
   *
   * Admin does not need to load grants because it has
   * unrestricted frontend access.
   */
  useEffect(() => {
    let active = true;

    async function loadPermissions() {
      /**
       * No authenticated user/role yet.
       */
      if (!role) {
        if (active) {
          setPermissions([]);
          setError(null);
          setLoading(false);
        }

        return;
      }

      /**
       * Admin bypass.
       */
      if (isAdmin) {
        if (active) {
          setPermissions([]);
          setError(null);
          setLoading(false);
        }

        return;
      }

      setLoading(true);
      setError(null);

      try {
        const response = await getRoleGrants();

        const extractedPermissions = extractPermissions(response);

        if (active) {
          setPermissions(
            extractedPermissions
          );
        }
      } catch (requestError) {
        console.error(
          "[PermissionProvider] Failed to load permissions:",
          requestError
        );

        console.error(
          "[PermissionProvider] Status:",
          requestError?.response?.status
        );

        console.error(
          "[PermissionProvider] Response:",
          requestError?.response?.data
        );

        if (active) {
          setPermissions([]);
          setError(requestError);
        }
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    }

    loadPermissions();

    return () => {
      active = false;
    };
  }, [role, isAdmin]);

  /**
   * Creates a lookup map using the permissions returned
   * directly by the API.
   *
   * Example:
   *
   * GET /api/v1/managers => full
   *
   * GET /api/v1/reports/payroll => project-scoped
   *
   * GET /api/v1/rates/users => ownership
   */
  const permissionMap = useMemo(() => {
    if (isAdmin) {
      return new Map();
    }

    return buildPermissionMap(
      permissions
    );
  }, [permissions, isAdmin]);

  /**
   * Returns the access level associated with
   * an endpoint and HTTP method.
   *
   * @param {string} endpoint
   * @param {string} method
   * @returns {string|null}
   */
  const getGrant = useCallback(
    (endpoint, method) => {
      if (isAdmin) {
        return null;
      }

      const key = permissionKey(
        endpoint,
        method
      );

      const grant =
        permissionMap.get(key) ??
        null;

      return grant;
    },
    [
      permissionMap,
      isAdmin,
    ]
  );

  /**
   * Determines whether the current user has the
   * requested endpoint/method available.
   *
   * This is intended for:
   *
   * - routes
   * - menus
   * - pages
   *
   * It does NOT evaluate project/ownership scope.
   *
   * If the API returned the permission, the user can
   * access the resource/page.
   *
   * @param {string} endpoint
   * @param {string} method
   * @returns {boolean}
   */
  const hasPermission = useCallback(
    (endpoint, method) => {
      if (isAdmin) {
        return true;
      }

      return (
        getGrant(
          endpoint,
          method
        ) !== null
      );
    },
    [
      isAdmin,
      getGrant,
    ]
  );

  /**
   * Evaluates whether the current user can perform
   * a specific action considering its scope.
   *
   * Access levels supported:
   *
   * - full
   * - ownership
   * - project-scoped
   *
   * @param {string} endpoint
   * @param {string} method
   * @param {object} scope
   * @returns {boolean}
   */
  const can = useCallback(
    (
      endpoint,
      method,
      scope = {}
    ) => {
      if (isAdmin) {
        return true;
      }

      const projects =
        getSessionItem("projects") ??
        [];

      const allowedProjectIds =
        projects
          .map(
            (project) =>
              project?.id ??
              project?.projectId ??
              project
          )
          .filter(
            (projectId) =>
              projectId !== null &&
              projectId !== undefined
          );

      const grant = getGrant(
        endpoint,
        method
      );

      return evaluateGrant(
        grant,
        {
          currentUserId,
          allowedProjectIds,
          ...scope,
        }
      );
    },
    [
      isAdmin,
      currentUserId,
      getGrant,
    ]
  );

  /**
   * Context value exposed to the application.
   */
  const value = useMemo(
    () => ({
      user,
      role,
      isAdmin,

      permissions,

      loading,
      error,

      getGrant,
      hasPermission,
      can,
    }),
    [
      user,
      role,
      isAdmin,

      permissions,

      loading,
      error,

      getGrant,
      hasPermission,
      can,
    ]
  );

  return (
    <PermissionContext.Provider
      value={value}
    >
      {children}
    </PermissionContext.Provider>
  );
}

/**
 * Hook used to access the permission context.
 *
 * Must be used inside PermissionProvider.
 *
 * @returns {{
 *   user: object|null,
 *   role: string,
 *   isAdmin: boolean,
 *   permissions: Array,
 *   loading: boolean,
 *   error: any,
 *   getGrant: Function,
 *   hasPermission: Function,
 *   can: Function
 * }}
 */
export function usePermissions() {
  const context =
    useContext(PermissionContext);

  if (!context) {
    throw new Error(
      "usePermissions must be used inside PermissionProvider"
    );
  }

  return context;
}